import { supabase } from "./client";
import {
  buildBranchPaymentSetupSnapshot,
  normalizePaymentProvider,
  resolveBranchCommissionRateByChannel,
} from "../local/paymentSettings";

const MENU_PAYMENT_FUNCTION_NAME = "mp-create-menu-payment";

const toSafeText = (value, maxLength = 160) =>
  String(value || "")
    .trim()
    .slice(0, maxLength);

const toMoney = (value) => {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? Math.round(parsed * 100) / 100 : 0;
};

const normalizeOrderIdList = (value) =>
  Array.from(
    new Set(
      (Array.isArray(value) ? value : [])
        .map((entry) => toSafeText(entry, 160))
        .filter(Boolean)
    )
  ).slice(0, 24);

const buildPendingOrderSnapshot = (order = {}) => ({
  id: toSafeText(order?.id, 160),
  mesa: toSafeText(order?.mesa || order?.tableLabel, 120),
  tableId: toSafeText(order?.tableId, 120),
  qrToken: toSafeText(order?.qrToken, 240),
  sessionKey: toSafeText(order?.sessionKey, 240),
  customerName: toSafeText(order?.customerName, 80),
  customerSessionId: toSafeText(order?.customerSessionId, 160),
  comensales: Math.max(1, Number(order?.comensales || 1)),
  canal: toSafeText(order?.canal || "QR", 40),
  source: toSafeText(order?.source || "app", 40),
  requestedPaymentMethod: toSafeText(
    order?.requestedPaymentMethod || order?.requestedMethod || "deep_link",
    60
  ),
  selectedPaymentMethod: toSafeText(
    order?.selectedPaymentMethod || order?.selectedMethod || "deep_link",
    60
  ),
  paymentStatus: toSafeText(order?.paymentStatus || "link_sent", 40),
  kitchenModeSnapshot: toSafeText(order?.kitchenModeSnapshot || "manual", 40),
  phase: toSafeText(order?.phase || "new", 40),
  stockDiscounted: order?.stockDiscounted === true,
  orderedAt: toSafeText(order?.orderedAt, 80),
  updatedAt: toSafeText(order?.updatedAt, 80),
  notes: toSafeText(order?.notes, 500),
  totalAmount: toMoney(order?.totalAmount ?? order?.pricing?.finalTotal),
  pricing:
    order?.pricing && typeof order.pricing === "object" ? order.pricing : null,
  paymentOperationId: toSafeText(order?.paymentOperationId, 160),
  items: (Array.isArray(order?.items) ? order.items : []).slice(0, 80),
});

const normalizeLinkPayload = (value) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const kind = toSafeText(value.kind, 80).toLowerCase();
  const deeplinkUrl = toSafeText(value.deeplinkUrl || value.linkUrl, 1200);
  const providerCheckoutId = toSafeText(value.providerCheckoutId, 160);

  if (!deeplinkUrl) {
    return null;
  }

  return {
    kind: kind || "checkout_link",
    deeplinkUrl,
    linkUrl: deeplinkUrl,
    providerCheckoutId,
  };
};

const normalizeFunctionError = (error, data, fallbackMessage) => {
  const transportMessage = toSafeText(error?.message, 240);
  const backendMessage = toSafeText(
    data?.error || data?.message || data?.details || data?.hint,
    240
  );
  const combinedMessage = `${backendMessage} ${transportMessage}`.toLowerCase();

  if (
    combinedMessage.includes("duplicate key") &&
    combinedMessage.includes("provider_external_reference")
  ) {
    return "Ya habia un cobro anterior para esta cuenta. Actualiza las cuentas pendientes y vuelve a intentar.";
  }

  if (backendMessage && transportMessage.toLowerCase().includes("non-2xx")) {
    return backendMessage;
  }

  return backendMessage || transportMessage || fallbackMessage;
};

const extractFunctionErrorPayload = async (error, data) => {
  if (data && typeof data === "object") {
    return data;
  }

  const response = error?.context;

  if (!response) {
    return null;
  }

  const readableResponse =
    typeof response.clone === "function" ? response.clone() : response;

  try {
    const contentType = toSafeText(
      readableResponse.headers?.get?.("content-type"),
      120
    ).toLowerCase();

    if (contentType.includes("application/json")) {
      return await readableResponse.json();
    }

    const rawText = toSafeText(await readableResponse.text(), 500);

    if (!rawText) {
      return null;
    }

    try {
      return JSON.parse(rawText);
    } catch {
      return { error: rawText };
    }
  } catch {
    return null;
  }
};

export const normalizeMenuDigitalPaymentIntent = (value = {}) => {
  const provider = normalizePaymentProvider(value.provider);
  const linkPayload = normalizeLinkPayload(value.linkPayload || value.qr || value.qrPayload);
  const deeplinkUrl =
    toSafeText(value.deeplinkUrl || value.linkUrl, 1200) ||
    linkPayload?.deeplinkUrl ||
    "";

  return {
    id: toSafeText(value.id || value.intentId || "", 120),
    status: toSafeText(value.status || "created", 40).toLowerCase(),
    provider,
    externalReference: toSafeText(
      value.externalReference || value.providerExternalReference || "",
      160
    ),
    providerOperationId: toSafeText(
      value.providerOperationId || value.providerIntentId || "",
      160
    ),
    grossAmount: toMoney(value.grossAmount),
    retainedAmount: toMoney(value.retainedAmount),
    merchantNetAmount: toMoney(value.merchantNetAmount),
    currentCommissionAmount: toMoney(value.currentCommissionAmount),
    pendingDebtAppliedAmount: toMoney(value.pendingDebtAppliedAmount),
    expiresAt: toSafeText(value.expiresAt || "", 80),
    createdAt: toSafeText(value.createdAt || "", 80),
    approvedAt: toSafeText(value.approvedAt || value.approved_at || "", 80),
    deeplinkUrl,
    qr: deeplinkUrl
      ? {
          ...(linkPayload || {}),
          kind: linkPayload?.kind || "checkout_link",
          deeplinkUrl,
          linkUrl: deeplinkUrl,
        }
      : null,
    raw: value,
  };
};

export const createMenuDigitalPaymentIntent = async ({
  branch,
  order,
  customerName = "",
  forceRefresh = false,
  walletProvider = "",
  sourceChannel = "menu_qr",
}) => {
  if (!supabase) {
    throw new Error(
      "Supabase no esta configurado. No podemos preparar el cobro digital del menu."
    );
  }

  const setup = buildBranchPaymentSetupSnapshot(branch);

  if (!setup.qrDigitalConfigured) {
    throw new Error(
      setup.qrDeepLinkBlockingReason ||
        "La sucursal todavia no esta lista para cobrar digitalmente desde el menu QR."
    );
  }

  const branchId = toSafeText(branch?.id || "", 80);
  const orderId = toSafeText(order?.id || "", 120);
  const pendingAccountOrderIds = normalizeOrderIdList(
    order?.pendingAccountOrderIds
  );
  const normalizedSourceChannel = toSafeText(
    sourceChannel || order?.source || "menu_qr",
    40
  ).toLowerCase();
  const isPublicMenuSource = !["kiosk", "waiter"].includes(
    normalizedSourceChannel
  );

  if (!branchId || !orderId) {
    throw new Error(
      "Faltan datos basicos del pedido para generar el cobro digital del menu."
    );
  }

  const body = {
    branchId,
    orderId,
    paymentOperationId: toSafeText(order?.paymentOperationId || "", 120),
    provider: setup.paymentProvider,
    paymentAccountReference: setup.paymentAccountReference,
    paymentAccountAlias: setup.paymentAccountAlias,
    paymentAccountCbu: setup.paymentAccountCbu,
    commissionRate: resolveBranchCommissionRateByChannel(
      { ...branch, ...setup },
      "menu_qr"
    ),
    paymentSplitMode: setup.paymentSplitMode,
    walletProvider: toSafeText(
      walletProvider || order?.menuWalletProviderId || "",
      40
    ),
    customerName: toSafeText(customerName || order?.customerName || "", 80),
    grossAmount: toMoney(order?.totalAmount ?? order?.pricing?.finalTotal),
    tableLabel: toSafeText(order?.mesa || order?.tableLabel || "", 120),
    sourceChannel: normalizedSourceChannel || "menu_qr",
    paymentContext: toSafeText(
      order?.paymentContext ||
        (pendingAccountOrderIds.length
          ? "table_pending_accounts"
          : "immediate_order"),
      60
    ),
    pendingAccountOrderIds,
    qrToken: toSafeText(order?.qrToken, 240),
    customerSessionToken: toSafeText(order?.customerSessionId, 160),
    pendingOrderSnapshot:
      isPublicMenuSource && !pendingAccountOrderIds.length
        ? buildPendingOrderSnapshot(order)
        : null,
    forceRefresh: forceRefresh === true,
    returnUrl:
      typeof window !== "undefined" ? toSafeText(window.location.href, 1400) : "",
  };

  const { data, error } = await supabase.functions.invoke(
    MENU_PAYMENT_FUNCTION_NAME,
    { body }
  );

  if (error) {
    const errorPayload = await extractFunctionErrorPayload(error, data);

    throw new Error(
      normalizeFunctionError(
        error,
        errorPayload,
        "No pudimos preparar el cobro digital del menu."
      )
    );
  }

  return normalizeMenuDigitalPaymentIntent(data);
};
